import { format, getDay, getHours } from "date-fns";
import type { BucketSize } from "@/lib/analytics/types";
import type { WindowDays } from "@/lib/github/types";

/**
 * Group items by time-bucket key. Key format depends on bucket:
 *   day   → "yyyy-MM-dd"  (e.g. "2026-04-22")
 *   week  → "yyyy-'W'II"  (e.g. "2026-W17", ISO week)
 *   month → "yyyy-MM"     (e.g. "2026-04")
 * Keys are sorted ascending in the output Map.
 */
export function bucketByPeriod<T>(
  items: T[],
  bucket: BucketSize,
  getDate: (item: T) => Date | null | undefined,
): Map<string, T[]> {
  const fmt = bucketFmt(bucket);
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const d = getDate(item);
    if (!d) continue;
    const key = format(d, fmt);
    const list = buckets.get(key);
    if (list) {
      list.push(item);
    } else {
      buckets.set(key, [item]);
    }
  }
  return new Map([...buckets.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

/**
 * Resolve the automatic bucket size from the window length.
 * Switch is exhaustive over all 5 WindowDays values — TS verifies via `never`.
 */
/** date-fns format string for a given bucket — keep in sync with bucketByPeriod. */
export function bucketFmt(bucket: BucketSize): string {
  if (bucket === "day") return "yyyy-MM-dd";
  if (bucket === "week") return "yyyy-'W'II";
  return "yyyy-MM";
}

/** Human-readable noun for the bucket ("day" / "week" / "month"). */
export function bucketWord(bucket: BucketSize): string {
  if (bucket === "day") return "day";
  if (bucket === "week") return "week";
  return "month";
}

/** Lowercase adverbial form of the bucket ("daily" / "weekly" / "monthly"). */
export function bucketAdverb(bucket: BucketSize): "daily" | "weekly" | "monthly" {
  if (bucket === "day") return "daily";
  if (bucket === "week") return "weekly";
  return "monthly";
}

/**
 * Enumerate every period key between `start` and `end` (inclusive) for the
 * given bucket size. Returns keys in chronological order, deduplicated.
 *
 * Used to surface empty periods in dashboards where tabular layouts (e.g.
 * the evolution matrix) need to show a column per week even if the repo
 * had zero runs that week. A simple day-by-day walk + bucketFmt keeps the
 * logic identical to `bucketByPeriod`'s keying without re-implementing ISO
 * week math — day / week / month all collapse naturally as duplicates.
 */
export function enumeratePeriods(
  start: Date,
  end: Date,
  bucket: BucketSize,
): string[] {
  if (!(start instanceof Date) || !(end instanceof Date)) return [];
  const startMs = new Date(start).setHours(0, 0, 0, 0);
  const endMs = new Date(end).setHours(0, 0, 0, 0);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return [];
  }
  const fmt = bucketFmt(bucket);
  const out: string[] = [];
  const seen = new Set<string>();
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  for (let ms = startMs; ms <= endMs; ms += ONE_DAY_MS) {
    const key = format(new Date(ms), fmt);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

export function resolveBucket(windowDays: WindowDays): BucketSize {
  switch (windowDays) {
    case 7:
      return "day";
    case 30:
      return "day";
    case 45:
      return "day";
    case 90:
      return "week";
    case 180:
      return "week";
    case 400:
      return "month";
    default: {
      const _exhaustive: never = windowDays;
      return _exhaustive;
    }
  }
}

/**
 * 0 = Monday … 6 = Sunday. We pick ISO week ordering so the heatmap matches
 * the ISO week bucket labels used elsewhere (yyyy-'W'II) and aligns with what
 * most engineering teams expect (work week starts Monday).
 */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface DayHourKey {
  dayOfWeek: DayOfWeek;
  hour: number;
}

/**
 * Converts JS getDay() (0 = Sunday) to our Monday-first convention.
 */
function toMondayFirst(jsDay: number): DayOfWeek {
  return ((jsDay + 6) % 7) as DayOfWeek;
}

/**
 * Group items by (dayOfWeek, hour). `dayOfWeek` is Monday-first (0=Mon…6=Sun).
 */
export function bucketByDayHour<T>(
  items: T[],
  getDate: (item: T) => Date | null | undefined,
  useUTC = false,
): Map<string, { key: DayHourKey; items: T[] }> {
  const buckets = new Map<string, { key: DayHourKey; items: T[] }>();
  for (const item of items) {
    const d = getDate(item);
    if (!d) continue;
    const jsDay = useUTC ? d.getUTCDay() : getDay(d);
    const dayOfWeek = toMondayFirst(jsDay);
    const hour = useUTC ? d.getUTCHours() : getHours(d);
    const mapKey = `${dayOfWeek}-${hour}`;
    const bucket = buckets.get(mapKey);
    if (bucket) {
      bucket.items.push(item);
    } else {
      buckets.set(mapKey, { key: { dayOfWeek, hour }, items: [item] });
    }
  }
  return buckets;
}
